import { Injectable } from '@angular/core';
import { CurrentDashboardProfileContext, DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import { sortByProfileTypeObj, VisibleProfileType } from '@rma/dashboard/generic/domain/to-be-sorted';
import { NavigationService } from '@rma/dashboard/generic/util/navigation';
import {
  ProfileNotificationModel,
  UserNotificationEventType,
  UserNotificationModel,
} from '@rma/dashboard/notification-bell/private/api-user-notifications';
import { isIndividual } from '@rma/generic/domain/helpers';
import { ProfileType } from '@rma/generic/domain/types';
import { take } from 'rxjs';
import { NotificationBellEventService } from './notification-bell-event.service';
import { NotificationRouteData } from './notification-route';

@Injectable({
  providedIn: 'root',
})
export class NotificationBellNavigationService {
  constructor(
    private readonly notificationBellEventService: NotificationBellEventService,
    private readonly profileContextService: DashboardProfileContextService,
    private readonly navigationService: NavigationService,
  ) {}

  public navigateTo(notification: UserNotificationModel, routeData: NotificationRouteData) {
    this.profileContextService.profileContext$.pipe(take(1)).subscribe((context) => {
      const newContext = this.moveIntoCorrectDashboardContext(context, notification.profileNotifications);
      this.notificationBellEventService.markAs(notification, UserNotificationEventType.Click).subscribe();
      const urmParams = {
        utm_source: 'notification-bell',
        utm_medium: 'web',
      };

      const contextRoute = isIndividual(newContext) ? routeData.agentRoute : routeData.agencyRoute;
      this.navigationService.navigateTo(contextRoute.path, {
        ...urmParams,
        ...contextRoute.params,
      });
    });
  }

  private moveIntoCorrectDashboardContext(
    currentContext: CurrentDashboardProfileContext,
    notificationContexts: ProfileNotificationModel[],
  ): { profileCode: string; profileType: ProfileType } {
    if (!notificationContexts?.length) return currentContext;

    const isCurrentContextInNotificationContexts = !!notificationContexts.find(
      (x) => x.profileType === currentContext.profileType && x.profileCode === currentContext.profileCode,
    );

    if (isCurrentContextInNotificationContexts) {
      return currentContext;
    }

    const visibleContexts = notificationContexts.filter(
      (x): x is ProfileNotificationModel & { profileType: VisibleProfileType } => x.profileType !== 'Network',
    );
    if (!visibleContexts.length) {
      return currentContext;
    }
    const swapIntoContext = sortByProfileTypeObj(visibleContexts, 'desc')[0];
    this.profileContextService.setProfileContext(swapIntoContext.profileType, swapIntoContext.profileCode);

    return swapIntoContext;
  }
}
